import { forwardRef, type Ref, useState } from 'react';
import { styled } from '@mui/material/styles';
import { getParameterByName } from '../../utils/urlUtils';
import Button from '../foundation/Button';
import Alert from '../foundation/Alert';
import Anchor from '../foundation/Anchor';
import { LoginPopup } from './LoginPopup';
import type { GoogleAuthFlow } from '../../../common/googleAuthFlow';
import clientConfig from '../config.client';

export type GoogleLoginProps = {
    flow: GoogleAuthFlow
    smartAgentByActivity?: boolean
    showToEmployers?: boolean
    isEmployer?: boolean
    isAllowedToOpenPopup?: () => boolean | Promise<boolean>
    phone?: string
    onAuthSuccess?: () => void | Promise<void>
    onRegistrationRequired?: () => void
    onEmailConflict?: () => void
    onExistingAccountEmailUnverified?: () => void
    buttonText?: string
    disabled?: boolean
    appearance?: 'default' | 'outline'
    showGoogleIcon?: boolean
}

const GoogleIcon = (): JSX.Element => (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path fill="#4285F4" d="M21.6 12.23c0-.78-.07-1.53-.2-2.23H12v4.22h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.52z" />
        <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.45l-3.24-2.51c-.9.6-2.04.96-3.38.96-2.6 0-4.8-1.76-5.6-4.12H3.06v2.59A9.99 9.99 0 0 0 12 22z" />
        <path fill="#FBBC05" d="M6.4 13.88A6.01 6.01 0 0 1 6.08 12c0-.65.11-1.28.32-1.88V7.53H3.06A9.99 9.99 0 0 0 2 12c0 1.61.39 3.14 1.06 4.47l3.34-2.59z" />
        <path fill="#EA4335" d="M12 6c1.47 0 2.78.5 3.82 1.5l2.87-2.87C16.95 3.01 14.69 2 12 2a9.99 9.99 0 0 0-8.94 5.53l3.34 2.59C7.2 7.76 9.4 6 12 6z" />
    </svg>
);

const GoogleLoginStyled = styled('div', {
    shouldForwardProp: prop => prop !== '$appearance',
})<{ $appearance: 'default' | 'outline' }>(({ theme, $appearance }) => ({
    '.MuiButtonBase-root': $appearance === 'outline'
        ? {
            backgroundColor: '#ffffff',
            border: `1px solid ${theme.palette.primary.main}`,
            color: theme.palette.primary.main,
        }
        : {
            backgroundColor: '#c71610',
        },
    '.MuiButtonBase-root:hover': $appearance === 'outline'
        ? {
            backgroundColor: 'rgba(52, 126, 62, 0.06)',
        }
        : undefined,
}));

const GoogleLogin = forwardRef((props: GoogleLoginProps, ref: Ref<HTMLButtonElement>): JSX.Element => {
    const [popupKey, setPopupKey] = useState<number | null>(null);
    const [fallbackNotice, setFallbackNotice] = useState<
    'emailConflict' | 'unverifiedAccount' | null
    >(null);
    const openPopup = () => {
        setPopupKey(prev => (prev ?? 0) + 1);
    };

    const onLoginClick = async () => {
        setFallbackNotice(null);
        if (!props.isAllowedToOpenPopup) {
            openPopup();
            return;
        }

        const isAllowed = props.isAllowedToOpenPopup();
        if (typeof isAllowed === 'boolean') {
            if (isAllowed) openPopup();
            return;
        }

        if (await isAllowed) openPopup();
    };
    const onUserClosePopup = () => { setPopupKey(null); };

    const onLogin = async () => {
        setPopupKey(null);
        if (props.onAuthSuccess) {
            await props.onAuthSuccess();
            return;
        }
        const redirectUrl = getParameterByName('redirect');
        if (redirectUrl && redirectUrl !== '') {
            window.location.assign(redirectUrl);
        } else {
            window.location.reload();
        }
    };

    const onRegistrationRequired = () => {
        setPopupKey(null);
        props.onRegistrationRequired?.();
    };

    const onEmailConflict = () => {
        setPopupKey(null);
        if (props.onEmailConflict) {
            props.onEmailConflict();
            return;
        }
        setFallbackNotice('emailConflict');
    };

    const onExistingAccountEmailUnverified = () => {
        setPopupKey(null);
        if (props.onExistingAccountEmailUnverified) {
            props.onExistingAccountEmailUnverified();
            return;
        }
        setFallbackNotice('unverifiedAccount');
    };

    const buildLoginUrl = () => {
        const params = new URLSearchParams();
        params.append('flow', props.flow);
        params.append('smartAgentByActivity', props.smartAgentByActivity ? '1' : '0');
        if (props.showToEmployers !== undefined) {
            params.append('showToEmployers', props.showToEmployers ? '1' : '0');
        }
        if (props.isEmployer) {
            params.append('isEmployer', '1');
        }
        if (props.phone) {
            params.append('phone', props.phone);
        }
        return `/login/gmail?${params.toString()}`;
    };

    return <>
        {popupKey !== null && <LoginPopup
            key={popupKey}
            onUserClosePopup={onUserClosePopup}
            onPopupClose={onLogin}
            onRegistrationRequired={onRegistrationRequired}
            onEmailConflict={onEmailConflict}
            onExistingAccountEmailUnverified={onExistingAccountEmailUnverified}
            location={buildLoginUrl()}
        />}
        {fallbackNotice === 'emailConflict' && <Alert severity="error">
            <span>
                קיים חשבון המשויך לכתובת המייל הזו ולא ניתן לחבר אותו אוטומטית לחשבון Google. אנא צרו איתנו קשר ב־
                <Anchor
                    href={`mailto:${clientConfig.contact.email}`}
                    text={clientConfig.contact.email}
                />.
            </span>
        </Alert>}
        {fallbackNotice === 'unverifiedAccount' && <Alert severity="warning">
            קיים חשבון עם כתובת המייל הזו שטרם אומת. יש להתחבר באמצעות המייל והסיסמה ולאמת
            את כתובת המייל בעמוד הפרופיל; לאחר מכן יהיה ניתן להתחבר גם באמצעות Google.
        </Alert>}
        <GoogleLoginStyled $appearance={props.appearance || 'default'}>
            <Button
                ref={ref}
                onClick={onLoginClick}
                disabled={props.disabled}
                startIcon={props.showGoogleIcon ? <GoogleIcon /> : undefined}
                type="button"
                title={props.buttonText || 'התחברות עם Gmail'}
                size="large" fullWidth>{props.buttonText || 'התחברות עם Gmail'}</Button>
        </GoogleLoginStyled>
    </>;
});

export default GoogleLogin;
